import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import {
  clearPortalUiFlags,
  DEFAULT_PORTAL_UI_FLAGS,
  normalizePortalUiFlags,
  readPortalUiFlags,
  writePortalUiFlags,
} from '../portal-oauth/uiFlags';

const PortalUiContext = createContext(null);

export function PortalUiProvider({ children }) {
  const { user, loading } = useAuth();
  const [flags, setFlagsState] = useState(() => readPortalUiFlags());
  const prevUserRef = useRef(undefined);

  const setPortalUiFlags = useCallback((next) => {
    const normalized = writePortalUiFlags(next);
    setFlagsState(normalized);
    return normalized;
  }, []);

  const resetPortalUiFlags = useCallback(() => {
    clearPortalUiFlags();
    setFlagsState({ ...DEFAULT_PORTAL_UI_FLAGS });
  }, []);

  const releasePortalChromeOnLogout = useCallback(() => {
    const keepDisablePlatform = readPortalUiFlags().isDisablePlatform === true;
    clearPortalUiFlags();
    if (!keepDisablePlatform) {
      setFlagsState({ ...DEFAULT_PORTAL_UI_FLAGS });
      return;
    }
    const normalized = writePortalUiFlags({
      isShowSidenav: true,
      isDisablePlatform: true,
    });
    setFlagsState(normalized);
  }, []);

  // Clear sidenav override only on logout (user → null), not on initial anonymous load
  // so /portal_oauth can set flags before auth completes.
  // isDisablePlatform is kept so the sign-in heading stays hidden.
  useEffect(() => {
    if (loading) return;
    const hadUser = Boolean(prevUserRef.current);
    prevUserRef.current = user;
    if (hadUser && !user) {
      releasePortalChromeOnLogout();
    }
  }, [loading, user, releasePortalChromeOnLogout]);

  const value = useMemo(
    () => ({
      ...normalizePortalUiFlags(flags),
      setPortalUiFlags,
      resetPortalUiFlags,
    }),
    [flags, setPortalUiFlags, resetPortalUiFlags]
  );

  return (
    <PortalUiContext.Provider value={value}>{children}</PortalUiContext.Provider>
  );
}

export function usePortalUi() {
  const ctx = useContext(PortalUiContext);
  if (!ctx) {
    throw new Error('usePortalUi must be used within a PortalUiProvider');
  }
  return ctx;
}
